"use client";

import FeatureTemplate from "@/components/feature-template";
import type {
  DetailedFeatureItem,
  FeatureHeroTab,
  TestimonialItem,
} from "@/components/feature-template/types";
import { FileCheck2, Gauge, LayoutGrid } from "lucide-react";
import { faqs } from "./content";

const IMG = "/assets/images/ai-agents/live-apply";
const APP = "/autoapply";

const heroFeatures: FeatureHeroTab[] = [
  {
    id: "live-jobs",
    name: "Live Jobs",
    title: "See Jobs Minutes After They Post",
    description:
      "Live Apply searches the boards in real time and lists the newest matching roles first, each with how long ago it was posted and how well it fits.",
    imageSrcUrl: `${IMG}/hero-live-jobs.png`,
    primaryActionText: "Go Live",
    primaryActionLink: APP,
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
  {
    id: "filters",
    name: "Precise Filters",
    title: "Filter Down to the Last 15 Minutes",
    description:
      "Narrow by date posted, experience level, work type and distance, and on LinkedIn show only roles with fewer applicants. Filters are saved for each board.",
    imageSrcUrl: `${IMG}/hero-filters.png`,
    primaryActionText: "Go Live",
    primaryActionLink: APP,
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
  {
    id: "bulk-apply",
    name: "Bulk Apply",
    title: "Select the Best, Apply in One Click",
    description:
      "Tick the jobs you want and Live Apply works through them, filling every form from your profile. Pause or resume whenever you like.",
    imageSrcUrl: `${IMG}/hero-bulk-apply.png`,
    primaryActionText: "Go Live",
    primaryActionLink: APP,
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
];

const featureGroup1: DetailedFeatureItem[] = [
  {
    id: "platforms",
    title: "Six Job Boards, One Search",
    subtitle: "STOP SWITCHING TABS",
    description:
      "Search LinkedIn, Naukri, Indeed, Foundit, Instahyre and Internshala from one place, one board at a time or all together.",
    benefits: [
      "Six boards supported",
      "One search across all of them",
      "Board-specific filters",
      "One apply button",
    ],
    primaryCTA: "Search Live Jobs",
    primaryLink: APP,
    imageSrcUrl: `${IMG}/platforms.png`,
    icon: <LayoutGrid className="w-4 h-4" />,
    stats: "6 job boards",
  },
  {
    id: "match-scores",
    title: "A Match Score on Every Job",
    subtitle: "SPEND TIME ON THE RIGHT ROLES",
    description:
      "Every live job is scored against your profile. Sort by match, and hover any score to see how skills, experience, location and keywords contribute.",
    benefits: [
      "Score on every listing",
      "Breakdown on hover",
      "Sort by best fit",
      "Skip weak matches fast",
    ],
    primaryCTA: "See My Matches",
    primaryLink: APP,
    secondaryCTA: "Score My Resume",
    secondaryLink: "/features/resume-jobfit-scorer",
    imageSrcUrl: `${IMG}/match-scores.png`,
    icon: <Gauge className="w-4 h-4" />,
    stats: "Scored against your profile",
    orientation: "reversed",
  },
];

const featureGroup2: DetailedFeatureItem[] = [
  {
    id: "tailored-attach",
    title: "The Right Resume, Attached",
    subtitle: "TAILORED ON LINKEDIN AND INDEED",
    description:
      "On LinkedIn and Indeed, Live Apply attaches a resume tailored to that job, with the headline, projects and skills adjusted to the posting.",
    benefits: [
      "Tailored PDF per application",
      "Headline matched to the role",
      "Relevant projects first",
      "Master resume unchanged",
    ],
    primaryCTA: "Go Live",
    primaryLink: APP,
    secondaryCTA: "Build My Resume",
    secondaryLink: "/features/smart-resume-builder",
    imageSrcUrl: `${IMG}/tailored-attach.png`,
    icon: <FileCheck2 className="w-4 h-4" />,
    stats: "Tailored resume attached",
  },
];

// Replace with real user quotes before publishing.
const testimonials: TestimonialItem[] = [
  {
    id: 1,
    text: "I filter to roles posted in the last hour and apply before lunch. My applications are now in the first ten instead of the last hundred.",
    name: "Jalen Kyle",
    role: "Project Manager",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 2,
    text: "Searching LinkedIn and Naukri from one screen saves me an hour every morning, and the match score tells me which ones are worth it.",
    name: "Marcus T.",
    role: "DevOps Engineer",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 3,
    text: "I select eight jobs, press apply and make coffee. It fills every form and attaches a tailored resume on LinkedIn.",
    name: "Priya M.",
    role: "Marketing Manager",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&h=800&fit=crop&q=80",
  },
];

export default function LiveApplyAgentPageClient() {
  return (
    <FeatureTemplate
      type="type1"
      sections={[
        {
          type: "hero",
          data: {
            title: "Be First in Line.",
            titleHighlight: "Every Time.",
            subtitle:
              "Recruiters read the earliest applications first. Live Apply finds roles minutes after they are posted across six job boards, scores each one against your profile and applies to your picks in one click.",
            ctaText: "Go Live",
            ctaLink: APP,
            features: heroFeatures,
          },
        },
        {
          type: "features",
          data: {
            sectionTitle: "Faster to Find. Faster to Apply.",
            sectionSubtitle:
              "Every major board in one place, with a score that shows what is worth your time.",
            features: featureGroup1,
          },
        },
        { type: "features", data: { features: featureGroup2 } },
        {
          type: "testimonials",
          data: {
            title: "First to Apply, First to Interview",
            subtitle: "From job seekers who got there before the crowd",
            testimonials,
          },
        },
        {
          type: "faq",
          data: {
            title: "Questions About Live Apply",
            subtitle: "Boards, filters and how applying works.",
            faqs,
            ctaTitle: "Ready to apply before everyone else?",
            ctaText: "Go Live",
            ctaLink: APP,
          },
        },
      ]}
    />
  );
}
